/**
 * The five guide cards.
 *
 * WHAT IS DATA HERE AND WHAT IS NOT — this is the whole point of the component:
 *
 *   TITLE and SUMMARY of all five      static copy (the catalogue's `support:docs.*`)
 *   DESTINATION of three of them       static copy too since the launch: the website's guide pages,
 *                                      `https://moonx.app/for-authors/<slug>` (ruling N15 of
 *                                      `docs/design/27-launch-texts/01-decisions.md`).
 *   DESTINATION of the fifth           LIVE: `TermsState.contentUrl` from `getCurrentTerms`.
 *   ACCEPTANCE STATE of the fifth      LIVE: `accepted`, `acceptedAt`, `isMaterialChange`.
 *
 * A card whose `href` is `null` is still rendered as a card with no destination — dashed, not a
 * link, and saying the guide is not published yet - so a guide added before its page exists is NOT
 * drawn as a link that goes nowhere (the prototype's five rows carry `cursor:pointer` and a
 * hover border with no `onClick` and no `href`), and it is not silently dropped either: the titles
 * are the owner's own and tell an author what the programme covers even before the pages exist.
 */
import { Badge, Button, Group, Stack, Text, VisuallyHidden } from '@mantine/core';
import { ChevronRight, FileText, ListChecks, Search, Users, Video, Wallet } from '@moonx/ui';
import { Trans, useTranslation } from '../../../i18n';
import type { TermsState } from '../api/contract';
import type { AsyncResource } from '../hooks/use-async-resource';
import { formatDay, formatDayAndTime } from '../format';
import { SUPPORT_DOCS, TERMS_DOC_KEY } from '../strings';
import type { SupportDoc } from '../strings';
import { LoadingBlock } from './StateBlocks';
import { OffsiteMark } from './OffsiteMark';
import classes from '../support.module.css';

/*
 * ★ DESIGN 15: each card is the prototype's - the guide's icon in a round well at the left, drawn by
 * `CardHead`, and an arrow at the right of a card that goes somewhere. `DOC_ICON` is at the foot.
 */
function CardHead({ doc }: { readonly doc: SupportDoc }) {
  const { t } = useTranslation('support');
  return (
    <>
      <DocIcon doc={doc} />
      <Text className={classes.docTitle}>{t(`docs.${doc.key}.title`)}</Text>
      <Text size="xs" c="dimmed">
        {t(`docs.${doc.key}.summary`)}
      </Text>
    </>
  );
}

/** One of the three with no known destination, or one that has been given a URL in the catalogue. */
function StaticGuideCard({ doc }: { readonly doc: SupportDoc }) {
  const { t } = useTranslation('support');
  if (doc.href === null) {
    return (
      <div className={`${classes.docCard} ${classes.docCardInert}`}>
        <CardHead doc={doc} />
        <Text size="xs" c="dimmed" mt="xs" fs="italic">
          {t('page.docNoDestination')}
        </Text>
      </div>
    );
  }

  return (
    <a
      className={`${classes.docCard} ${classes.docCardLinked}`}
      href={doc.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <DocIcon doc={doc} />
      <Text className={classes.docTitle}>
        {t(`docs.${doc.key}.title`)}
        <OffsiteMark />
        {/* The mark is decorative; this is what a screen reader hears instead. */}
        <VisuallyHidden> ({t('page.docOpensOffsite')})</VisuallyHidden>
      </Text>
      <Text size="xs" c="dimmed">
        {t(`docs.${doc.key}.summary`)}
      </Text>
      <ChevronRight size={15} className={classes.docArrow} aria-hidden="true" />
    </a>
  );
}

/**
 * The one card with live data behind it.
 *
 * It is a `div` and not a link, because it holds an anchor AND (when acceptance is owed) a button,
 * and an interactive element inside a link is neither reachable nor announced correctly.
 */
function TermsGuideCard({ doc, terms, onReviewAndAccept }: TermsGuideCardProps) {
  const { t } = useTranslation('support');
  return (
    <div className={classes.docCard}>
      <CardHead doc={doc} />

      {terms.status === 'loading' ? (
        <div style={{ marginTop: 'var(--mx-space-6)' }}>
          <LoadingBlock label={t('page.termsLoading')} lines={1} height={12} />
        </div>
      ) : null}

      {/* Any failure degrades the card to its static self, and that is the right answer rather than
          an error box: the GUIDE has not changed, only our live link to it is missing. A 401 is the
          shell's to handle and is caught a level up; `getCurrentTerms` declares no 403 at all, so a
          403 here would be the API contradicting its own contract and still must not blank a help
          page. The retry lives on the page, not on every card, so one press refetches once. */}
      {terms.status === 'failed' ? (
        <Text size="xs" c="dimmed" mt="xs">
          {t('page.termsUnavailable')}
        </Text>
      ) : null}

      {terms.status === 'ready' && terms.data !== null ? (
        <Stack gap="xs" mt="sm">
          <Group gap="xs" align="center">
            <Badge
              size="sm"
              variant="light"
              color={terms.data.accepted ? 'gray' : 'moon'}
              aria-label={
                terms.data.accepted
                  ? t('page.termsAcceptedBadge')
                  : t('page.termsActionNeededBadge')
              }
            >
              {terms.data.accepted
                ? t('page.termsAcceptedBadge')
                : t('page.termsActionNeededBadge')}
            </Badge>
            <Text size="xs" c="dimmed">
              <Trans
                t={t}
                i18nKey="page.termsVersionLine"
                values={{
                  version: terms.data.versionLabel,
                  day: formatDay(terms.data.effectiveFrom),
                }}
                components={{ time: <time dateTime={terms.data.effectiveFrom} /> }}
              />
            </Text>
          </Group>

          {terms.data.accepted && typeof terms.data.acceptedAt === 'string' ? (
            <Text size="xs" c="dimmed">
              <Trans
                t={t}
                i18nKey="page.termsAcceptedOn"
                values={{ moment: formatDayAndTime(terms.data.acceptedAt) }}
                components={{ time: <time dateTime={terms.data.acceptedAt} /> }}
              />
            </Text>
          ) : null}

          <Group gap="sm">
            <a
              href={terms.data.contentUrl}
              target="_blank"
              rel="noopener noreferrer"
              /* Mantine's Anchor would style this; a plain anchor inherits the card's colour, which
                 is what the brand's own card links do. The offsite mark carries the meaning. */
              style={{ fontSize: 'var(--mantine-font-size-xs)', fontWeight: 600 }}
            >
              {t('page.termsOpenDocument')}
              <OffsiteMark />
              <VisuallyHidden> ({t('page.docOpensOffsite')})</VisuallyHidden>
            </a>
            {terms.data.accepted ? null : (
              <Button size="compact-xs" variant="filled" onClick={onReviewAndAccept}>
                {t('page.termsReviewAndAccept')}
              </Button>
            )}
          </Group>
        </Stack>
      ) : null}
    </div>
  );
}

export interface GuideCardsProps {
  readonly terms: AsyncResource<TermsState>;
  readonly onReviewAndAccept: () => void;
}

export function GuideCards({ terms, onReviewAndAccept }: GuideCardsProps) {
  return (
    <Stack gap="sm" component="ul" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {SUPPORT_DOCS.map((doc) => (
        <li key={doc.key}>
          {doc.key === TERMS_DOC_KEY ? (
            <TermsGuideCard doc={doc} terms={terms} onReviewAndAccept={onReviewAndAccept} />
          ) : (
            <StaticGuideCard doc={doc} />
          )}
        </li>
      ))}
    </Stack>
  );
}

/* ---------------------------------------------------- written at the foot, on purpose */

// Below the components because GuideCards.tsx (the terms card's live link) is cited by number.

interface TermsGuideCardProps {
  readonly doc: SupportDoc;
  readonly terms: AsyncResource<TermsState>;
  readonly onReviewAndAccept: () => void;
}

/** Each guide's icon (design 15) - the prototype's, or its nearest in web/ui/src/icons.ts. */
const DOC_ICON: Readonly<Record<string, typeof FileText>> = {
  [TERMS_DOC_KEY]: ListChecks,
  'how-review-works': Search,
  'how-payments-work': Wallet,
  'community-rules': Users,
  'video-guidelines': Video,
};

/** The round well at the left of every card; a guide added later gets the page icon. */
function DocIcon({ doc }: { readonly doc: SupportDoc }) {
  const Icon = DOC_ICON[doc.key] ?? FileText;
  return (
    <span className={classes.docIcon} aria-hidden="true">
      <Icon size={16} />
    </span>
  );
}
